import { CbhIcon, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack, TextField } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useFormContext } from "react-hook-form";

import { type PostShiftReviewFormValues, QuestionTag, type StepAction } from "../types";

export interface QuestionStepProps {
  questionTag: QuestionTag.SOFTWARE | QuestionTag.SUPPLIES | QuestionTag.PATIENTS_PER_NURSE;
  questionText: string;
  options?: Array<{ id: string; displayText: string }>;
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function QuestionStep({
  questionTag,
  questionText,
  options,
  isSubmitting = false,
  onAction,
}: QuestionStepProps) {
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();

  // Get the appropriate form field values based on question tag
  function getFormFieldValues() {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        return {
          answer: watch("softwareAnswer"),
          otherText: watch("softwareOtherText") ?? "",
        };
      }

      case QuestionTag.SUPPLIES: {
        return {
          answer: watch("suppliesAnswer"),
          otherText: "",
        };
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        return {
          answer: watch("patientCountAnswer"),
          otherText: "",
        };
      }

      default: {
        return { answer: undefined, otherText: "" };
      }
    }
  }

  const { answer: selectedAnswer, otherText } = getFormFieldValues();
  const showOtherInput = selectedAnswer === "Other";

  const iconMap = {
    [QuestionTag.SOFTWARE]: "software",
    [QuestionTag.SUPPLIES]: "supplies",
    [QuestionTag.PATIENTS_PER_NURSE]: "patients",
  } as const;

  // Use provided options from API, fallback to empty array if not provided
  const displayOptions = options?.map((opt) => opt.displayText) ?? [];

  const setFormFieldValue = (field: "answer" | "otherText", value: string | undefined) => {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        if (field === "answer") {
          setValue("softwareAnswer", value, { shouldValidate: true });
        } else {
          setValue("softwareOtherText", value ?? "", { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.SUPPLIES: {
        if (field === "answer") {
          setValue("suppliesAnswer", value, { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        if (field === "answer") {
          setValue("patientCountAnswer", value, { shouldValidate: true });
        }

        break;
      }

      default: {
        // No form field updates for unrecognized question tags
        break;
      }
    }
  };

  function clearFormFields() {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        setValue("softwareAnswer", undefined, { shouldValidate: true });
        setValue("softwareOtherText", "", { shouldValidate: true });
        break;
      }

      case QuestionTag.SUPPLIES: {
        setValue("suppliesAnswer", undefined, { shouldValidate: true });
        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        setValue("patientCountAnswer", undefined, { shouldValidate: true });
        break;
      }

      default: {
        // No form field clearing for unrecognized question tags
        break;
      }
    }
  }

  const handleOptionSelect = (option: string) => {
    if (selectedAnswer === option) {
      // Unselect by clicking again
      clearFormFields();
    } else {
      setFormFieldValue("answer", option);
      if (option !== "Other") {
        setFormFieldValue("otherText", "");
      }
    }
  };

  const isValid =
    isDefined(selectedAnswer) && (selectedAnswer !== "Other" || otherText.trim().length > 0);

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        <Stack spacing={6} sx={{ px: 0 }}>
          <Stack alignItems="center" spacing={4}>
            <CbhIcon type={iconMap[questionTag]} size="large" />
            <Title variant="h3" component="h3" textAlign="center">
              {questionText}
            </Title>
          </Stack>

          <Stack spacing={3} sx={{ pt: 5 }}>
            {displayOptions.map((option) => (
              <SelectableBox
                key={option}
                variant="primary"
                startIconType="building"
                endIconType={selectedAnswer === option ? "checkmark" : undefined}
                isSelected={selectedAnswer === option}
                disabled={isSubmitting}
                sx={{
                  "& > div": {
                    textAlign: "left",
                    ml: 3,
                  },
                }}
                onToggleSelect={() => {
                  handleOptionSelect(option);
                }}
              >
                {option}
              </SelectableBox>
            ))}
          </Stack>

          {showOtherInput && (
            <TextField
              placeholder="Documentation software name"
              value={otherText}
              disabled={isSubmitting}
              onChange={(event) => {
                setFormFieldValue("otherText", event.target.value);
              }}
            />
          )}
        </Stack>
      </DialogContent>

      {!isDefined(selectedAnswer) && !isSubmitting && (
        <DialogFooter orientation="vertical">
          <TextButton
            sx={{ alignSelf: "center" }}
            onClick={() => {
              onAction({ type: "SKIP_STEP" });
            }}
          >
            Skip
          </TextButton>
        </DialogFooter>
      )}

      {isDefined(selectedAnswer) && (
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isSubmitting}
            disabled={!isValid}
            onClick={() => {
              onAction({
                type: "NEXT_STEP",
                data: {
                  answer: selectedAnswer,
                  otherText: selectedAnswer === "Other" ? otherText : undefined,
                },
              });
            }}
          >
            Continue
          </LoadingButton>
        </DialogFooter>
      )}
    </>
  );
}
